import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { Link } from "react-router-dom";

export default function TesterDashboard() {
  const { currentUser, loading } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [debugResults, setDebugResults] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      setLoadingInvites(true);
      try {
        // fetch invites for this email and include game info
        const { data, error } = await supabase
          .from("invites")
          .select("id, game_id, email, created_at, accepted_at, games(id, name)")
          .eq("email", currentUser.email)
          .order("created_at", { ascending: false });
        if (error) throw error;
        setInvites(data || []);
        console.debug("TesterDashboard: invites for", currentUser.email, data);
      } catch (e) {
        console.error("Failed to load invites:", e);
      } finally {
        setLoadingInvites(false);
      }
    })();
  }, [currentUser?.id, currentUser?.email]);

  const runDebugChecks = async () => {
    if (!currentUser) return setDebugResults({ error: 'Not signed in' });
    setDebugResults({ running: true });
    try {
      const email = currentUser.email;
      const [{ data: invites, error: iErr }, { data: testers, error: tErr }] = await Promise.all([
        supabase.from('invites').select('*').eq('email', email),
        supabase.from('testers').select('*').eq('email', email),
      ]);

      // gather games mentioned in invites
      const gameIds = Array.from(new Set((invites || []).map(i => i.game_id)));
      const gamesPromises = gameIds.length ? await supabase.from('games').select('*').in('id', gameIds) : { data: [] };

      setDebugResults({ invites, testers, games: gamesPromises.data || [] });
      console.debug('Tester debug', { invites, testers, games: gamesPromises.data || [] });
    } catch (e) {
      console.error('Debug checks failed', e);
      setDebugResults({ error: String(e) });
    }
  };

  const copyDebugResults = async () => {
    if (!debugResults) return;
    try {
      const text = JSON.stringify(debugResults, null, 2);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        alert('Copied debug JSON to clipboard');
      } else {
        const t = document.createElement('textarea');
        t.value = text;
        document.body.appendChild(t);
        t.select();
        document.execCommand('copy');
        document.body.removeChild(t);
        alert('Copied debug JSON to clipboard');
      }
    } catch (e) {
      console.error('Copy failed', e);
      alert('Copy failed: ' + String(e?.message || e));
    }
  };

  const downloadDebugResults = () => {
    if (!debugResults) return;
    const blob = new Blob([JSON.stringify(debugResults, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tester-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const accept = async (invite) => {
    try {
      const acceptedAt = new Date().toISOString();

      // Mark invite as accepted
      const { error: iErr } = await supabase
        .from("invites")
        .update({ accepted_at: acceptedAt })
        .eq("id", invite.id);

      if (iErr) throw iErr;

      // Add to testers whitelist
      const { error: tErr } = await supabase
        .from("testers")
        .upsert(
          { game_id: invite.game_id, email: invite.email },
          { onConflict: ["game_id", "email"] }
        );

      if (tErr) throw tErr;

      setInvites((prev) =>
        prev.map((i) =>
          i.id === invite.id ? { ...i, accepted_at: acceptedAt } : i
        )
      );
    } catch (e) {
      console.error("Failed to accept invite:", e);
      alert("Failed to accept invite");
    }
  };

  const decline = async (invite) => {
    try {
      // Remove invite
      const { error: iErr } = await supabase
        .from("invites")
        .delete()
        .eq("id", invite.id);

      if (iErr) throw iErr;

      // Just in case there is a testers row, remove it too
      const { error: tErr } = await supabase
        .from("testers")
        .delete()
        .eq("game_id", invite.game_id)
        .eq("email", invite.email);

      if (tErr) console.warn("Failed to remove tester on decline:", tErr);

      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (e) {
      console.error("Failed to decline invite:", e);
      alert("Failed to decline invite");
    }
  };


  if (loading) return null;

  return (
    <div className="ml-0 md:ml-0 flex-1 flex flex-col justify-start bg-[#111] text-[#eee] font-nunito">


      <div className="flex flex-col items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tester Dashboard</h1>
          <h2 className="text-lg font-semibold mt-6">My Games</h2>
          <p className="text-sm text-[#aaa] mt-2">
            If you were invited, accept to be added to the approved testers list. Once accepted you can access playtests for the game via the Play link.
          </p>
        </div>
      </div>


      {/* <div className="mb-3 text-sm text-[#aaa]">Signed in as: <strong>{currentUser?.email}</strong></div> */}
      {/* <section className="bg-[#151515] border border-[#252525] rounded-xl p-4 md:p-5 mt-6 space-y-4"> */}
        {/* <h2 className="text-lg font-semibold">Games</h2> */}
        {/* <div className="mb-4">
        <button onClick={runDebugChecks} className="px-3 py-1.5 rounded border border-[#64748b] text-[#cbd5e1]">Run debug checks</button>
      </div> */}
        {loadingInvites ? (
          <div>Loading…</div>
        ) : invites.length === 0 ? (
          <div className="text-sm text-[#777] mt-8">You have no tester invitations.</div>
        ) : (
          <ul className="space-y-5 mt-5">
            {invites.map((inv) => (
              <li key={inv.id} className="w-full text-left rounded-xl border border-[#252525] bg-[#111] px-4 py-3 hover:border-[#8338ec] hover:bg-[#181818] transition flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{inv.games?.name ?? inv.game_id}</div>
                  <div className="flex items-center gap-2">
                    {inv.accepted_at ? (
                      <>
                        {/* <span className="text-sm text-green-300">Accepted</span> */}
                        <Link
                          to={`/play?game=${inv.game_id}`}
                          className="text-sm px-3 py-1.5 rounded-lg border border-[#10b981] bg-[#10b981]/20 hover:bg-[#10b981] text-white transition"
                        >
                          Play
                        </Link>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => accept(inv)}
                          className="px-3 py-1.5 rounded border border-[#10b981] text-[#10b981]"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => decline(inv)}
                          className="px-3 py-1.5 rounded border border-[#ef4444] text-[#ef4444]"
                        >
                          Decline
                        </button>
                      </>
                    )}
                  </div>

                </div>
              </li>
            ))}
          </ul>
        )}

      {/* </section> */}

    </div>
  );
}
